import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import cn from 'classnames';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { ICountryProductsList, PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Arrow from '@/public/icons/arrow-forward.svg';
import Image from 'next/image';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { GEOGRAPHY_OF_BUSINESS_PAGE_QUERY } from '@/data/geographyOfBusinessPageQuery';
import { Table } from '@/components/common/Table';
import { useCurrentDeviceHeighPicker, useMediaQueryWidthPicker } from '@/hooks';
import { TableMobile } from '@/components/common/TableMobile';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { AnimatedComponent } from '@/components/common/AnimatedComponent';
import { PageHead } from '@/components/common/PageHead';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { title, seo, featuredImage } = data.pageBy.translation;
  const { allCountries } = data;
  const [countryProducts, setCountryProducts] = useState<
    ICountryProductsList[] | undefined
  >();

  const featureImage = featuredImage?.node;
  const countriesList = allCountries?.nodes;

  const { device } = useMediaQueryWidthPicker();

  const minH = useCurrentDeviceHeighPicker() - 110;

  const isUkraineCountry = (countryTitle?: string) =>
    countryTitle === 'Україна' || countryTitle === 'Ukraine';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toggleProductsList = (countryId: any) => {
    setSelectedCountry(prevCountry =>
      prevCountry === countryId ? null : countryId,
    );
    const countryProductsList = countriesList.find(
      item => item.id === countryId,
    )?.countryCard?.countryProductsList;

    countryProducts != countryProductsList
      ? setCountryProducts(countryProductsList)
      : setCountryProducts(undefined);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Section
            // className={cn(`min-h-[${minH}px]`)}
            style={{
              minHeight: `${minH}px`,
            }}
            breadcrumbs={seo?.breadcrumbs}
            lazy={false}
          >
            {title && (
              <Heading
                tag="h1"
                withDivider
                content={title}
                className="mb-[60px]"
              />
            )}
            <div
              className={cn('grid gap-15', {
                'lg:grid-cols-[.3fr,_.7fr] lg:gap-[120px]': !selectedCountry,
                'lg:grid-cols-[auto,_954px] lg:gap-9': selectedCountry,
              })}
            >
              <ul className="grid gap-8 md:grid-cols-2 md:gap-10 lg:w-max lg:grid-cols-1 lg:self-start">
                {countriesList.map(item => {
                  const isUkraine = isUkraineCountry(item.title);

                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        disabled={!isUkraine}
                        className={cn(
                          'grid grid-cols-[28px_1fr] items-center gap-2',
                          isUkraine && 'text-accentMain',
                          selectedCountry == item.id &&
                            'dark:dark:gray-scale-red-diff text-accentMain',
                          !isUkraine && 'cursor-default',
                        )}
                        onClick={() => {
                          if (isUkraine) {
                            toggleProductsList(item.id);
                          }
                        }}
                      >
                        <Arrow
                          className={cn(
                            'h-3 w-3',
                            isUkraine ? 'text-accentMain' : 'text-current',
                          )}
                        />
                        <span className="text-h2Mobile">{item.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {featureImage && (
                <div className={`${!selectedCountry ? 'block' : 'hidden'}`}>
                  <Image
                    src={featureImage?.mediaItemUrl ?? ''}
                    alt={featureImage?.altText ?? ''}
                    width={featureImage?.mediaDetails?.width}
                    height={featureImage?.mediaDetails?.height}
                  />
                </div>
              )}
              {countryProducts && (
                <div className="overflow-x-auto md:max-w-full lg:w-[950px]">
                  {device == 'mobile' ? (
                    <TableMobile
                      data={countryProducts.map(product => product.item)}
                    />
                  ) : (
                    <AnimatedComponent
                      variants={{
                        hidden: { opacity: 0, zoom: 1.2 },
                        visible: { opacity: 1, zoom: 1 },
                      }}
                    >
                      <Table
                        dataArr={countryProducts.map(product => product.item)}
                        className="text-base"
                      />
                    </AnimatedComponent>
                  )}
                </div>
              )}
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const countryLanguage = language;
  const { data } = await client.query({
    query: GEOGRAPHY_OF_BUSINESS_PAGE_QUERY,
    variables: {
      language,
      countryLanguage,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
